// D-1046: 全ルートの画面利用計測(page_usage_stats)用の page_key 表。
//
// ★App.jsx の <Route path> 1 本につき 1 行。src/__tests__/app/routeUsageCoverageD1046.test.js が
//   App.jsx を読み、「表に無いルート」「App.jsx に無い表の行」を赤にする(ルートを足したらここも足す)。
// ★key の付け方
//   - 画面 = 'page:' + snake_case(feat: / flow: と同じ「接頭辞:名前」の名前空間)。
//     ★D-068 の分析ページ 18 画面は接頭辞なし(forecast 等)で既に計測中。D-068 のハブ並び替え
//       (AdminReportsHubPage の TILES key で引く)がその素の key を見ているので、名前空間を分けて
//       新しい行が並び替えに混ざらないようにする。
//   - key: null = 中身を描かずに別の URL へ移るだけのルート(<Navigate>)。計測しない。
//   - self: true = 画面の中で useTrackPageUsage(D-068)が既に計測している。二重計上しないよう
//     ルート側では数えない(key は画面側と同じ値を書いておく = 一覧で引けるように)。
// ★URL の値(:boothCode 等)は key に入れない。key はパターンから決まる固定の文字列だけ。
import { PAGE_KEY } from './pageKeys'

export const SCREEN_PAGE_KEY_PREFIX = 'page:'
const FLOW_PAGE_KEY_PREFIX = 'flow:'

// 巡回の段取り時間(ルートの移り変わりから測る。巡回画面のコードには触らない)。
export const FLOW_KEY = {
  // ブース入力画面に入ってから、次のブースへ進むまで(スワイプ/保存して次へ)。1 ブースの所要時間。
  PATROL_BOOTH_NEXT: `${FLOW_PAGE_KEY_PREFIX}patrol_booth_next`,
  // ブース入力画面に入ってから、店舗のブース一覧へ戻るまで(最後のブース・一覧へ戻る)。
  PATROL_BOOTH_TO_LIST: `${FLOW_PAGE_KEY_PREFIX}patrol_booth_to_list`,
}

const p = (name) => `${SCREEN_PAGE_KEY_PREFIX}${name}`

/** @type {{ path: string, key: string|null, self?: boolean }[]} */
export const ROUTE_PAGE_KEYS = [
  // ── ログイン・入口 ──
  // ログイン前は staffId が無いので数えない。ログインが成立した瞬間(setSession → /launcher へ移る前)に
  // 1 回数えることがある = 実質「ログイン回数」。
  { path: '/login', key: p('login') },
  { path: '/invite', key: p('invite') },
  { path: '/launcher', key: p('launcher') },
  { path: '/', key: null },
  { path: '/input', key: null },
  { path: '/dashboard', key: p('dashboard_top') },
  { path: '/help', key: p('help') },
  { path: '/help/first-patrol', key: p('help_first_patrol') },
  { path: '/settings/face-id', key: p('settings_face_id') },

  // ── 集金 ──
  { path: '/collection/input', key: p('collection_input') },
  { path: '/collection/input-bulk', key: p('collection_input_bulk') },
  { path: '/collection/history', key: p('collection_history') },
  { path: '/collection/reconciliation', key: p('collection_reconcile') },
  { path: '/collection/prep', key: p('collection_prep') },

  // ── クレサポ(巡回)──
  { path: '/clawsupport', key: p('clawsupport_hub') },
  { path: '/clawsupport/alerts', key: p('patrol_alerts') },
  { path: '/clawsupport/changer/:machineCode', key: p('changer_input') },
  { path: '/clawsupport/route', key: p('patrol_route') },
  { path: '/clawsupport/schedule', key: p('patrol_schedule') },
  { path: '/clawsupport/store/:storeCode', key: p('patrol_store') },
  { path: '/clawsupport/booth/:boothCode', key: p('patrol_booth_input') },
  { path: '/clawsupport/store/:storeCode/dash', key: p('clawsupport_store_dash') },
  { path: '/clawsupport/store/:storeCode/daily', key: PAGE_KEY.DAILY_SALES_PAYOUT, self: true },
  { path: '/clawsupport/store/:storeCode/patrol', key: p('patrol_screen_v1') },
  { path: '/booth/:machineId', key: null },
  { path: '/drafts', key: null },
  { path: '/complete', key: null },
  { path: '/machines/:storeId', key: null },
  { path: '/patrol/overview', key: null },
  { path: '/patrol', key: null },
  { path: '/patrol/input', key: null },
  { path: '/patrol/booth', key: null },
  { path: '/manual/:modelId', key: p('manual_view') },
  { path: '/ocr-test', key: p('ocr_test') },

  // ── タナサポ(発注・入荷・在庫・棚卸し)──
  { path: '/tanasupport', key: p('tanasupport_hub') },
  { path: '/tanasupport/orders', key: p('order_list') },
  { path: '/tanasupport/store/:storeCode', key: p('tanasupport_store_dashboard') },
  { path: '/tanasupport/location/:locationId/stocktake', key: p('stocktake_location_input') },
  { path: '/tanasupport/stocktake', key: p('stocktake_session') },
  { path: '/tanasupport/store/:storeCode/stocktake', key: null },
  { path: '/tanasupport/store/:storeCode/stocktake/:sessionId', key: null },
  { path: '/stock', key: p('stock_top') },
  { path: '/stock/hub', key: p('stock_location_hub') },
  { path: '/stock/count', key: p('stock_count') },
  { path: '/stock/dashboard', key: p('stock_dashboard') },
  { path: '/stock/move', key: p('stock_move') },
  { path: '/stock/theoretical', key: p('stock_theoretical') },
  { path: '/stock/out', key: p('stock_out') },
  { path: '/stock/arrival', key: p('stock_arrival_check') },
  { path: '/stock/arrival-pending', key: p('stock_arrival_pending') },
  { path: '/stock/arrival-staff', key: p('stock_arrival_staff') },
  { path: '/stock/stocktake', key: p('stocktake_target') },
  { path: '/stock/stocktake/session', key: p('stock_stocktake_session') },
  { path: '/stock/stocktake/vehicle', key: p('stocktake_vehicle') },
  { path: '/stock/orders', key: p('stock_order_list') },
  { path: '/stock/ocr-count-test', key: p('stock_ocr_count_test') },
  { path: '/stock/announcements', key: p('stock_announcements') },

  // ── マネサポ(/admin の入れ子。AdminLayout の中)──
  { path: '/admin', key: null },                     // index → masters へ移るだけ
  { path: '/admin/manage', key: p('admin_manage_hub') },
  { path: '/admin/masters', key: p('admin_masters_hub') },
  { path: '/admin/masters/store-list', key: p('master_store_list') },
  { path: '/admin/masters/stores', key: p('master_store') },
  { path: '/admin/masters/prizes', key: p('master_prize') },
  { path: '/admin/masters/staff', key: p('master_staff') },
  { path: '/admin/masters/suppliers', key: p('master_supplier') },
  { path: '/admin/masters/abbrev-rules', key: p('master_prize_abbrev_rules') }, // D-1045
  { path: '/admin/masters/primary-staff', key: p('master_primary_staff') }, // D-1136 主担当の一覧
  { path: '/admin/masters/*', key: p('admin_placeholder') },
  { path: '/admin/audit', key: p('admin_audit_hub') },
  { path: '/admin/activity', key: p('audit_activity_log') },
  { path: '/admin/audit/orders', key: p('audit_order_history') },
  { path: '/admin/audit/booth-edit', key: p('audit_machine_list') },
  { path: '/admin/audit/booth-edit/:storeCode/machines', key: p('audit_machine_list') },
  { path: '/admin/audit/booth-edit/:boothCode', key: p('audit_booth_edit') },
  { path: '/admin/audit/collection-edit', key: p('audit_collection_edit') },
  { path: '/admin/audit/collection-edit/c/:collectionId', key: p('audit_collection_booth_edit') },
  { path: '/admin/audit/operations', key: p('audit_operation_logs') },
  { path: '/admin/audit/logins', key: p('audit_login_logs') },
  { path: '/admin/audit/prize-phases', key: p('audit_prize_phases') },
  { path: '/admin/audit/stock-moves', key: p('audit_stock_moves') },
  { path: '/admin/audit/bulk-import', key: p('audit_bulk_import') },
  { path: '/admin/audit/daily-stats', key: p('audit_daily_stats') },
  { path: '/admin/audit/store-rounds', key: p('audit_store_rounds') },
  { path: '/admin/audit/*', key: p('admin_placeholder') },
  { path: '/admin/reports', key: p('admin_reports_hub') },
  { path: '/admin/reports/booth-ranking', key: PAGE_KEY.BOOTH_RANKING, self: true },
  { path: '/admin/reports/payout-trend', key: PAGE_KEY.PAYOUT_TREND, self: true },
  { path: '/admin/reports/7dma', key: PAGE_KEY.DMA7, self: true },
  { path: '/admin/reports/prize-cost', key: PAGE_KEY.PRIZE_COST_RECOVERY, self: true },
  { path: '/admin/reports/store-comparison', key: PAGE_KEY.STORE_COMPARE, self: true },
  { path: '/admin/reports/profit-calendar', key: PAGE_KEY.PROFIT_CALENDAR, self: true },
  { path: '/admin/reports/collections', key: PAGE_KEY.COLLECTION_EXPORT, self: true },
  { path: '/admin/reports/collection-pdf-bulk', key: PAGE_KEY.COLLECTION_PDF_BULK, self: true },
  { path: '/admin/reports/monthly-target', key: PAGE_KEY.MONTHLY_TARGET, self: true },
  { path: '/admin/reports/daily-sales-payout', key: PAGE_KEY.DAILY_SALES_PAYOUT, self: true },
  { path: '/admin/reports/model-monthly-average', key: PAGE_KEY.MODEL_MONTHLY_AVG, self: true },
  { path: '/admin/reports/niceland-slip', key: PAGE_KEY.NICELAND_SLIP, self: true },
  { path: '/admin/reports/prize-payout', key: PAGE_KEY.PRIZE_PAYOUT_ANALYSIS, self: true }, // D-1072
  { path: '/admin/reports/*', key: p('admin_placeholder') },
  { path: '/admin/store', key: p('admin_store_hub') },
  { path: '/admin/store/:storeCode/daily', key: p('store_daily_report_list') },
  { path: '/admin/store/:storeCode/daily/:date', key: p('store_daily_report_form') },
  { path: '/admin/store/:storeCode/monthly', key: p('store_monthly_report_list') },
  { path: '/admin/store/:storeCode/monthly/:yearMonth', key: p('store_monthly_report_form') },
  { path: '/admin/store/:storeCode/shifts/:yearMonth', key: p('store_shifts') },
  { path: '/admin/store/:storeCode/tasks', key: p('store_tasks') },
  { path: '/admin/store/:storeCode/paid-leave', key: p('store_paid_leave_list') },
  { path: '/admin/store/:storeCode/paid-leave/:employeeId', key: p('store_paid_leave_detail') },
  { path: '/admin/store/:storeCode/roster', key: p('store_roster') },
  { path: '/admin/store/:storeCode/labor/:yearMonth', key: p('store_labor') }, // D-1112
  { path: '/admin/store/:storeCode/requests/:yearMonth', key: p('store_shift_requests') }, // D-1113
  { path: '/admin/store/*', key: p('admin_placeholder') },
  { path: '/admin/settings', key: p('admin_settings_hub') },
  { path: '/admin/settings/alerts', key: p('admin_alert_settings') }, // D-1121
  { path: '/admin/settings/*', key: p('admin_placeholder') },
  { path: '/admin/import', key: p('admin_import_hub') },
  { path: '/admin/collection', key: p('admin_collection_hub') },
  { path: '/admin/collection-flag', key: p('admin_collection_flag') },
  { path: '/admin/labels', key: null },
  { path: '/admin/*', key: p('admin_placeholder') },

  // ── マネサポ(/admin 直下の単独ルート。AdminLayout の外)──
  { path: '/admin/booth-edit/:boothCode', key: p('booth_edit_direct') },
  { path: '/admin/alert-types', key: p('admin_alert_types') },
  { path: '/admin/dev-assets', key: p('dev_assets_list') },
  { path: '/admin/dev-assets/upload', key: p('dev_assets_upload') },
  { path: '/datasearch', key: p('data_search') },
  { path: '/edit/:boothId', key: p('edit_reading') },
  { path: '/admin/meter-anomalies', key: p('meter_anomalies') },
  { path: '/admin/glossary', key: p('admin_glossary') },
  { path: '/admin/stocktake', key: p('admin_stocktake_list') },
  { path: '/admin/stocktake/create', key: p('admin_stocktake_create') },
  { path: '/admin/stocktake/dashboard', key: p('admin_stocktake_dashboard') },
  { path: '/admin/stocktake/:sessionId', key: p('admin_stocktake_detail') },
  { path: '/admin/stocktake/:sessionId/summary', key: p('admin_stocktake_summary') },
  { path: '/admin/lockers', key: p('admin_lockers') },
  { path: '/admin/tips', key: p('admin_tips') },
  { path: '/admin/collection-schedule', key: p('collection_schedule') },
  { path: '/admin/patrol-schedule-by-staff', key: p('patrol_schedule_by_staff') },
  { path: '/admin/provisional-prizes', key: p('provisional_prizes') },
  { path: '/admin/ops-watch', key: p('ops_watch') },
  { path: '/admin/feedback', key: p('app_feedback_list') },
  { path: '/admin/machine-inventory', key: p('machine_inventory') },
  { path: '/admin/forecast', key: PAGE_KEY.FORECAST, self: true },
  { path: '/admin/forecast/:storeCode', key: p('forecast_detail') },
  { path: '/admin/machine-ranking', key: PAGE_KEY.MACHINE_RANKING, self: true },
  { path: '/admin/per-diem', key: p('per_diem') },
  { path: '/admin/release-notes', key: p('release_notes') }, // D-1092
  { path: '/admin/reports/prize-ranking', key: PAGE_KEY.PRIZE_RANKING, self: true },
  { path: '/admin/ops-radar', key: PAGE_KEY.OPS_HEALTH_RADAR, self: true },
  { path: '/admin/order-ack', key: PAGE_KEY.ORDER_ACK_AUDIT, self: true },
  { path: '/admin/target-draft', key: PAGE_KEY.TARGET_DRAFT, self: true },
  { path: '/admin/models', key: p('admin_model_list') },
  { path: '/admin/machine-models', key: p('admin_machine_layout') },
  { path: '/admin/machines', key: p('admin_machine_list') },
  { path: '/admin/machine-links', key: p('admin_machine_model_link') },
  { path: '/admin/booths', key: p('admin_booth_list') },
  { path: '/admin/asset-bands', key: p('asset_bands') },
  { path: '/admin/asset-inventory', key: p('asset_inventory') },
  { path: '/admin/prize-links', key: p('prize_link') },
  { path: '/admin/manuals', key: p('manual_editor') },
  { path: '/admin/import-slips', key: p('import_slips') },
  { path: '/admin/setup-sheets', key: p('setup_sheets') },
  { path: '/admin/test-data', key: p('test_data_import') },

  // ── 受け皿 ──
  { path: '/*', key: null },                         // 未知の URL → /launcher へ移るだけ
]
